import React from 'react';
import { Flag, AlertTriangle, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const REPORT_TYPE_LABELS = {
  'inappropriate_content': 'Contenuto inappropriato',
  'false_information': 'Info false',
  'spam': 'Spam',
  'fake_profile': 'Profilo fake',
  'unprofessional_behavior': 'Comportamento scorretto',
  'other': 'Altro'
};

export const ReportsTab = ({ 
  reports, 
  reportsStats,
  reportsFilter,
  setReportsFilter,
  onOpenReportDetail,
  onReportReview
}) => {
  const filteredReports = reports.filter(r => reportsFilter === 'all' || r.status === reportsFilter);

  return (
    <div className="space-y-6">
      {/* Reports Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold">{reportsStats.total}</p>
          <p className="text-xs text-gray-500">Totale</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{reportsStats.pending}</p>
          <p className="text-xs text-gray-500">Da verificare</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{reportsStats.verified}</p>
          <p className="text-xs text-gray-500">Verificate</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-orange-600">{reportsStats.infractions}</p>
          <p className="text-xs text-gray-500">Infrazioni</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{reportsStats.null_reports}</p>
          <p className="text-xs text-gray-500">Segnalazioni nulle</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={reportsFilter} onValueChange={setReportsFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="pending">Da verificare</SelectItem>
            <SelectItem value="verified">Verificate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports List */}
      {filteredReports.length === 0 ? (
        <div className="text-center py-12">
          <Flag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessuna segnalazione</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Data</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Segnalatore</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Segnalato</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Contenuto</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Stato</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredReports
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map(report => (
                  <tr 
                    key={report.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onOpenReportDetail(report)}
                  >
                    <td className="px-4 py-3 text-sm">
                      {new Date(report.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium">{report.reporter_name}</p>
                      <p className="text-xs text-gray-500">{report.reporter_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium">{report.reported_user_name}</p>
                      <p className="text-xs text-gray-500">{report.reported_user_email}</p>
                      {report.reported_user_negative_points > 0 && (
                        <Badge variant="destructive" className="mt-1 text-xs">
                          {report.reported_user_negative_points} punti negativi
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{report.content_title || '-'}</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {report.content_type === 'service' ? 'Servizio' : 'Corso'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {REPORT_TYPE_LABELS[report.report_type] || report.report_type}
                      {report.description && (
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-[150px]">
                          "{report.description.substring(0, 30)}..."
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {report.status === 'pending' ? (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          Da verificare
                        </Badge>
                      ) : (
                        <Badge variant={report.outcome === 'infraction' ? 'destructive' : 'default'} className={report.outcome === 'null_report' ? 'bg-blue-100 text-blue-800' : ''}>
                          {report.outcome === 'infraction' ? 'Infrazione' : 'Segnalazione nulla'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {report.status === 'pending' ? (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => onReportReview(report.id, 'infraction', report.reporter_name, report.reported_user_name)}
                            title="Infrazione (punto negativo al segnalato)"
                            data-testid={`infraction-btn-${report.id}`}
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onReportReview(report.id, 'null_report', report.reporter_name, report.reported_user_name)}
                            title="Segnalazione nulla (punto negativo al segnalatore)"
                            data-testid={`null-report-btn-${report.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {report.reviewed_at ? new Date(report.reviewed_at).toLocaleDateString('it-IT') : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
